"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = require("react");
var _propTypes = _interopRequireDefault(require("prop-types"));
var _composeFunction = _interopRequireDefault(require("compose-function"));
var _reactQuery = require("react-query");
var _components = require("@folio/stripes/components");
var _core = require("@folio/stripes/core");
var _FormModal = _interopRequireDefault(require("../../FormModal/FormModal"));
var _CustomPropertiesLookup = _interopRequireDefault(require("./CustomPropertiesLookup"));
var _CustomPropertyView = _interopRequireDefault(require("./CustomPropertyView"));
var _CustomPropertyForm = _interopRequireDefault(require("./CustomPropertyForm"));
var _hooks = require("../../hooks");
var _utils = require("../../utils");
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const EDITING = 'edit';
const CREATING = 'create';
const VIEWING = 'view';

// A default option for setting up panes manually
const CustomPropertiesSettings = _ref => {
  let {
    afterQueryCalls,
    catchQueryCalls,
    contextFilterOptions,
    customPropertiesEndpoint,
    displayConditions = {
      create: true,
      delete: true,
      edit: true
    },
    helpPopovers,
    intlKey: passedIntlKey,
    intlNS: passedIntlNS,
    labelOverrides = {},
    refdataEndpoint
  } = _ref;
  const {
    create: createCondition = true,
    delete: deleteCondition = true,
    edit: editCondition = true
  } = displayConditions;
  const callout = (0, _react.useContext)(_core.CalloutContext);
  const queryClient = (0, _reactQuery.useQueryClient)();
  const [customProperty, setCustomProperty] = (0, _react.useState)();
  const [deleteModal, setDeleteModal] = (0, _react.useState)(false);
  const refdata = (0, _hooks.useRefdata)({
    endpoint: refdataEndpoint
  });
  const [mode, setMode] = (0, _react.useState)(VIEWING);
  const kintIntl = (0, _hooks.useKintIntl)(passedIntlKey, passedIntlNS);

  // Default props that need hooks are hard
  let renderContextFilterOptions = contextFilterOptions;
  if (!contextFilterOptions) {
    /*
      Default is {value: '', label: 'All'}, empty string will map to ALL contexts
      If null context is needed, use
      {value: 'isNull', label: ...}
    */
    renderContextFilterOptions = [{
      value: '',
      // empty string is all custoprop contexts
      label: kintIntl.formatKintMessage({
        id: 'customProperties.config.all',
        overrideValue: labelOverrides?.all
      })
    }];
  }
  const {
    post: createCustprop,
    put: editCustProp,
    delete: deleteCustProp
  } = (0, _hooks.useMutateCustomProperties)({
    afterQueryCalls: {
      put: res => {
        setMode(VIEWING);
        queryClient.invalidateQueries(['stripes-kint-components', 'useCustomProperties', 'custprops']);
        setCustomProperty(res);
        if (afterQueryCalls?.put) {
          afterQueryCalls.put(res);
        }
      },
      post: res => {
        setMode(VIEWING);
        queryClient.invalidateQueries(['stripes-kint-components', 'useCustomProperties', 'custprops']);
        setCustomProperty(res);
        if (afterQueryCalls?.post) {
          afterQueryCalls.post(res);
        }
      },
      delete: res => {
        setMode(VIEWING);
        queryClient.invalidateQueries(['stripes-kint-components', 'useCustomProperties', 'custprops']);
        setCustomProperty();
        if (afterQueryCalls?.delete) {
          afterQueryCalls.delete(res);
        }
      }
    },
    catchQueryCalls: {
      // Default delete behaviour is to fire a callout, either with kint-components default message
      // or one provided in labelOverrides, which is passed the error message and customProperty in question
      delete: async err => {
        const errorResp = await (0, _utils.parseErrorResponse)(err.response);
        callout.sendCallout({
          message: kintIntl.formatKintMessage({
            id: 'customProperties.config.delete.errorMessage',
            overrideValue: labelOverrides?.deleteError
          }, {
            error: errorResp?.message,
            label: customProperty?.label
          }),
          type: 'error'
        });
      },
      ...catchQueryCalls // Override defaults here
    },
    endpoint: customPropertiesEndpoint,
    id: customProperty?.id
  });
  const handeContextSubmit = submitData => {
    return {
      ...submitData,
      ctx: submitData?.ctx?.[0]?.value ?? ''
    };
  };

  // When POSTing a custom property, the backend expects a shorthand "Integer" or "LocalDate"
  // When PUTing a custom property, the backend needs the full class name
  const handleType = submitData => {
    const typeRegex = /(com\.k_int\.web\.toolkit\.custprops\.types\.CustomProperty)(.*)/g;
    const typeMatch = typeRegex.exec(submitData?.type) ?? [];
    return {
      ...submitData,
      type: typeMatch[2]
    };
  };
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Pane, {
      defaultWidth: "fill",
      id: "settings-customProperties.lookupPane",
      lastMenu: createCondition && /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Button, {
        buttonStyle: "primary",
        marginBottom0: true,
        onClick: () => setMode(CREATING) // TODO do we need to clear form here too?
        ,
        children: kintIntl.formatKintMessage({
          id: 'new',
          overrideValue: labelOverrides?.new
        })
      }),
      paneTitle: kintIntl.formatKintMessage({
        id: 'customProperties',
        overrideValue: labelOverrides.paneTitle
      }),
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_CustomPropertiesLookup.default, {
        contextFilterOptions: renderContextFilterOptions,
        customPropertiesEndpoint: customPropertiesEndpoint,
        intlKey: passedIntlKey,
        intlNS: passedIntlNS,
        labelOverrides: labelOverrides,
        onSelectCustomProperty: (_e, cp) => setCustomProperty(cp)
      })
    }), customProperty && /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Pane, {
      actionMenu: _ref2 => {
        let {
          onToggle
        } = _ref2;
        const actionsArray = [];
        if (editCondition) {
          actionsArray.push( /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Button, {
            buttonStyle: "dropdownItem",
            marginBottom0: true,
            onClick: () => setMode(EDITING),
            children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Icon, {
              icon: "edit",
              children: kintIntl.formatKintMessage({
                id: 'edit',
                overrideValue: labelOverrides?.edit
              })
            })
          }, `${customProperty.name}-action-edit`));
        }
        if (deleteCondition) {
          actionsArray.push( /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Button, {
            buttonStyle: "dropdownItem",
            marginBottom0: true,
            onClick: () => {
              setDeleteModal(true);
              onToggle();
            },
            children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Icon, {
              icon: "trash",
              children: kintIntl.formatKintMessage({
                id: 'delete',
                overrideValue: labelOverrides?.delete
              })
            })
          }, `${customProperty.name}-action-delete`));
        }
        return actionsArray?.length ? actionsArray : null;
      },
      defaultWidth: "fill",
      dismissible: true,
      id: "settings-customProperties-viewPane",
      onClose: () => setCustomProperty(),
      paneTitle: kintIntl.formatKintMessage({
        id: 'customProperties.config.viewPaneTitle',
        overrideValue: labelOverrides?.viewPaneTitle,
        fallbackMessage: customProperty?.label ?? customProperty?.name
      }, {
        name: customProperty?.label ?? customProperty?.name
      }),
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_CustomPropertyView.default, {
        customProperty: customProperty,
        helpPopovers: helpPopovers,
        intlKey: passedIntlKey,
        intlNS: passedIntlNS,
        labelOverrides: labelOverrides
      })
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_FormModal.default, {
      initialValues: mode === CREATING ? {
        weight: 0,
        primary: true,
        retired: false,
        defaultInternal: true
      } : {
        ...customProperty,
        ctx: customProperty?.ctx ? [{
          value: customProperty.ctx,
          label: customProperty.ctx
        }] : null,
        category: customProperty?.category ? customProperty?.category?.id : null
      },
      modalProps: {
        dismissible: true,
        label: mode === CREATING ? kintIntl.formatKintMessage({
          id: 'customProperties.config.newModal',
          overrideValue: labelOverrides?.newModalTitle
        }) : kintIntl.formatKintMessage({
          id: 'customProperties.config.editModal',
          overrideValue: labelOverrides?.editModalTitle
        }, {
          name: customProperty?.label ?? customProperty?.name
        }),
        onClose: () => setMode(VIEWING),
        open: mode === CREATING || mode === EDITING
      },
      onSubmit: mode === CREATING ? (0, _composeFunction.default)(createCustprop, handeContextSubmit, handleType) : (0, _composeFunction.default)(editCustProp, handeContextSubmit),
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_CustomPropertyForm.default, {
        contextFilterOptions: renderContextFilterOptions,
        helpPopovers: helpPopovers,
        intlKey: passedIntlKey,
        intlNS: passedIntlNS,
        labelOverrides: labelOverrides,
        refdata: refdata.map(rdc => ({
          label: rdc.desc,
          value: rdc.id
        }))
      })
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.ConfirmationModal, {
      buttonStyle: "danger",
      confirmLabel: kintIntl.formatKintMessage({
        id: 'delete',
        overrideValue: labelOverrides?.delete
      }),
      heading: kintIntl.formatKintMessage({
        id: 'customProperties.config.delete.confirmHeading',
        overrideValue: labelOverrides?.confirmHeading
      }),
      id: "delete-custprop-confirmation",
      message: kintIntl.formatKintMessage({
        id: 'customProperties.config.delete.confirmMessage',
        overrideValue: labelOverrides?.confirmMessage
      }, {
        name: customProperty?.label ?? customProperty?.name
      }),
      onCancel: () => setDeleteModal(false),
      onConfirm: () => {
        deleteCustProp(customProperty?.id);
        setCustomProperty();
        setDeleteModal(false);
      },
      open: deleteModal
    })]
  });
};
CustomPropertiesSettings.propTypes = {
  afterQueryCalls: _propTypes.default.object,
  catchQueryCalls: _propTypes.default.object,
  contextFilterOptions: _propTypes.default.arrayOf(_propTypes.default.shape({
    value: _propTypes.default.string,
    label: _propTypes.default.oneOfType([_propTypes.default.element, _propTypes.default.string])
  })),
  customPropertiesEndpoint: _propTypes.default.string,
  displayConditions: _propTypes.default.shape({
    create: _propTypes.default.bool,
    delete: _propTypes.default.bool,
    view: _propTypes.default.bool
  }),
  helpPopovers: _propTypes.default.object,
  intlKey: _propTypes.default.string,
  intlNS: _propTypes.default.string,
  labelOverrides: _propTypes.default.object,
  refdataEndpoint: _propTypes.default.string
};
var _default = exports.default = CustomPropertiesSettings;